import { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MoodChart = ({ darkMode = false }) => {
  const [moodData, setMoodData] = useState([]);
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('7');

  useEffect(() => {
    loadMoodData();
  }, [timeRange]);

  const loadMoodData = () => {
    const moodHistory = JSON.parse(localStorage.getItem('moodHistory') || '[]');
    
    // Filter data based on time range
    const days = parseInt(timeRange);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const filteredData = moodHistory.filter(mood => 
      new Date(mood.timestamp) >= cutoffDate
    ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    setMoodData(filteredData);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: darkMode ? '#e5e7eb' : '#374151',
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: `Mood Trend (Last ${timeRange} days)`,
        color: darkMode ? '#ffffff' : '#111827',
        font: {
          family: 'Inter, system-ui, sans-serif',
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        backgroundColor: darkMode ? '#374151' : '#ffffff',
        titleColor: darkMode ? '#ffffff' : '#111827',
        bodyColor: darkMode ? '#e5e7eb' : '#374151',
        borderColor: darkMode ? '#6b7280' : '#d1d5db',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        displayColors: false,
        callbacks: {
          title: function(context) {
            return new Date(context[0].label).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            });
          },
          label: function(context) {
            const mood = moodData[context.dataIndex];
            return `${mood.emoji} ${mood.label} (${mood.value}/5)`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
          color: darkMode ? '#9ca3af' : '#6b7280',
          font: {
            family: 'Inter, system-ui, sans-serif'
          },
          callback: function(value) {
            const moodLabels = {
              1: '😢 Very Sad',
              2: '😔 Sad',
              3: '😐 Neutral',
              4: '😊 Happy',
              5: '😄 Very Happy'
            };
            return moodLabels[value] || value;
          }
        },
        grid: {
          color: darkMode ? '#374151' : '#f3f4f6',
          borderColor: darkMode ? '#4b5563' : '#d1d5db'
        }
      },
      x: {
        ticks: {
          maxTicksLimit: 10,
          color: darkMode ? '#9ca3af' : '#6b7280',
          font: {
            family: 'Inter, system-ui, sans-serif'
          },
          callback: function(value, index) {
            if (moodData[index]) {
              return new Date(moodData[index].timestamp).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              });
            }
            return '';
          }
        },
        grid: {
          color: darkMode ? '#374151' : '#f3f4f6',
          borderColor: darkMode ? '#4b5563' : '#d1d5db'
        }
      }
    }
  };

  const chartData = {
    labels: moodData.map(mood => mood.timestamp),
    datasets: [
      {
        label: 'Mood Level',
        data: moodData.map(mood => mood.value),
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: chartType === 'line' 
          ? 'rgba(139, 92, 246, 0.1)' 
          : 'rgba(139, 92, 246, 0.8)',
        borderWidth: 3,
        fill: chartType === 'line',
        tension: 0.4,
        pointBackgroundColor: moodData.map(mood => {
          const colors = {
            1: '#ef4444', // red
            2: '#f97316', // orange
            3: '#eab308', // yellow
            4: '#22c55e', // green
            5: '#06b6d4'  // cyan
          };
          return colors[mood.value] || '#8b5cf6';
        }),
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8
      }
    ]
  };

  const getMoodStats = () => {
    if (moodData.length === 0) return null;
    
    const average = moodData.reduce((sum, mood) => sum + mood.value, 0) / moodData.length;
    const highest = Math.max(...moodData.map(mood => mood.value));
    const lowest = Math.min(...moodData.map(mood => mood.value));
    
    // Find most recent trend
    const recentMoods = moodData.slice(-3);
    let trend = 'stable';
    if (recentMoods.length >= 2) {
      const recentAvg = recentMoods.reduce((sum, mood) => sum + mood.value, 0) / recentMoods.length;
      const olderMoods = moodData.slice(0, -3);
      if (olderMoods.length > 0) {
        const olderAvg = olderMoods.reduce((sum, mood) => sum + mood.value, 0) / olderMoods.length;
        if (recentAvg > olderAvg + 0.3) trend = 'improving';
        else if (recentAvg < olderAvg - 0.3) trend = 'declining';
      }
    }
    
    return { average, highest, lowest, trend };
  };

  const stats = getMoodStats();

  const getMoodEmoji = (value) => {
    if (value >= 4.5) return '😄';
    if (value >= 3.5) return '😊';
    if (value >= 2.5) return '😐';
    if (value >= 1.5) return '😔';
    return '😢';
  };

  const getTrendEmoji = (trend) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      default: return '➡️';
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white font-sans">
          📈 Mood Chart
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Visualize your mood trends over time
        </p>
      </div>

      {/* Controls */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="wellness-input"
            >
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 3 months</option>
            </select>
            
            <select
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              className="wellness-input"
            >
              <option value="line">Line Chart</option>
              <option value="bar">Bar Chart</option>
            </select>
          </div>
        </div>
      </div>

      {moodData.length === 0 ? (
        <div className="wellness-card p-12 text-center">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Mood Data Yet
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            Start tracking your mood to see beautiful charts and insights here!
          </p>
        </div>
      ) : (
        <>
          {/* Statistics */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="wellness-card p-6 text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {getMoodEmoji(stats.average)} {stats.average.toFixed(1)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Average Mood
                </div>
              </div>
              
              <div className="wellness-card p-6 text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {getMoodEmoji(stats.highest)} {stats.highest}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Highest Mood
                </div>
              </div>
              
              <div className="wellness-card p-6 text-center">
                <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                  {getMoodEmoji(stats.lowest)} {stats.lowest}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Lowest Mood
                </div>
              </div>
              
              <div className="wellness-card p-6 text-center">
                <div className={`text-3xl font-bold ${
                  stats.trend === 'improving' ? 'text-green-600 dark:text-green-400' :
                  stats.trend === 'declining' ? 'text-red-600 dark:text-red-400' :
                  'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {getTrendEmoji(stats.trend)} {stats.trend}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Recent Trend
                </div>
              </div>
            </div>
          )}

          {/* Chart */}
          <div className="wellness-card p-6">
            <div style={{ height: '400px' }}>
              {chartType === 'line' ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <Bar data={chartData} options={chartOptions} />
              )}
            </div>
          </div>

          {/* Insights */}
          <div className="wellness-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 font-sans">
              💡 Insights
            </h3>
            <div className="space-y-3">
              {stats.trend === 'improving' && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">🎉</span>
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">Great progress!</p>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        Your mood has been improving recently. Keep up the great work!
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {stats.average >= 4 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">😊</span>
                    <div>
                      <p className="font-medium text-blue-800 dark:text-blue-200">Positive mindset!</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Your overall mood has been quite positive lately.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {moodData.length >= 7 && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">📊</span>
                    <div>
                      <p className="font-medium text-purple-800 dark:text-purple-200">Consistent tracking!</p>
                      <p className="text-sm text-purple-700 dark:text-purple-300">
                        You've logged {moodData.length} mood entries. Consistency helps identify patterns!
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MoodChart;
